import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import ImageUploadField from './ImageUploadField';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface Servicio {
  id: string;
  nombre: string;
  precio: string;
  oferta: string;
  oferta_activa: boolean;
  imagen: string;
}

const ServiciosPreciosTab = () => {
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'servicios')
        .maybeSingle();
      if (data?.value) {
        setServicios(
          ((data.value as { nombre: string; precio: string; oferta?: string; oferta_activa?: boolean; imagen?: string }[]) ?? [])
            .map((s, i) => ({
              id: `s-${i}`,
              nombre: s.nombre ?? '',
              precio: s.precio ?? '',
              oferta: s.oferta ?? '',
              oferta_activa: s.oferta_activa ?? false,
              imagen: s.imagen ?? '',
            }))
        );
      }
      setLoading(false);
    };
    load();
  }, []);

  const addServicio = () => {
    setServicios((p) => [...p, { id: `sv-${Date.now()}`, nombre: '', precio: '', oferta: '', oferta_activa: false, imagen: '' }]);
  };

  const removeServicio = (id: string) => setServicios((p) => p.filter((s) => s.id !== id));

  const updateServicio = <K extends keyof Servicio>(id: string, key: K, val: Servicio[K]) =>
    setServicios((p) => p.map((s) => s.id === id ? { ...s, [key]: val } : s));

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${SUPABASE_URL}/functions/v1/bali-save-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ updates: [{ key: 'servicios', value: servicios.map(({ id: _id, ...rest }) => rest) }] }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">Servicios y precios</h3>
          <p className="text-zinc-500 text-xs mt-0.5">{servicios.length} servicio(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addServicio}
            className="flex items-center gap-1.5 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-add-line" /> Añadir servicio
          </button>
          <button onClick={handleSave} disabled={saving}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 ${
              saved ? 'bg-green-500 text-white' : 'bg-yellow-400 hover:bg-yellow-300 text-black'
            }`}>
            <i className={saving ? 'ri-loader-4-line animate-spin' : saved ? 'ri-check-line' : 'ri-save-line'} />
            {saving ? 'Guardando…' : saved ? '¡Guardado!' : 'Guardar'}
          </button>
        </div>
      </div>

      {servicios.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-800 flex flex-col items-center justify-center py-20 text-zinc-600">
          <i className="ri-price-tag-3-line text-4xl mb-3" />
          <p className="text-sm">Sin servicios. Pulsa &quot;Añadir servicio&quot; para empezar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {servicios.map((s) => (
            <div key={s.id} className="bg-zinc-900 border border-zinc-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-zinc-800 text-zinc-400">
                    <i className="ri-tools-line text-sm" />
                  </div>
                  <span className="text-zinc-300 text-xs font-bold uppercase tracking-wider truncate max-w-xs">
                    {s.nombre || 'Nuevo servicio'}
                  </span>
                </div>
                <button onClick={() => removeServicio(s.id)}
                  className="w-7 h-7 flex items-center justify-center text-zinc-600 hover:text-red-400 cursor-pointer transition-colors flex-shrink-0">
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Nombre del servicio</label>
                  <input
                    value={s.nombre}
                    onChange={(e) => updateServicio(s.id, 'nombre', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Precio</label>
                  <input
                    value={s.precio}
                    placeholder="Ej: Desde 49 € / Gratis / Consultar"
                    onChange={(e) => updateServicio(s.id, 'precio', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors"
                  />
                </div>
              </div>

              <div className="mb-4">
                <ImageUploadField
                  label="Imagen del servicio"
                  value={s.imagen}
                  onChange={(val) => updateServicio(s.id, 'imagen', val)}
                />
              </div>

              <div className="flex items-center gap-3 pt-3 border-t border-zinc-800 flex-wrap">
                <div
                  onClick={() => updateServicio(s.id, 'oferta_activa', !s.oferta_activa)}
                  className={`w-9 h-5 rounded-full cursor-pointer relative transition-colors flex-shrink-0 ${s.oferta_activa ? 'bg-yellow-400' : 'bg-zinc-700'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.oferta_activa ? 'left-4' : 'left-0.5'}`} />
                </div>
                <span className="text-zinc-400 text-xs font-bold uppercase tracking-wider">Oferta activa</span>
                {s.oferta_activa && (
                  <input
                    value={s.oferta}
                    placeholder="Precio de oferta (ej: Desde 39 €)"
                    onChange={(e) => updateServicio(s.id, 'oferta', e.target.value)}
                    className="flex-1 min-w-0 bg-zinc-800 border border-yellow-400/40 text-yellow-300 text-sm px-3 py-1.5 outline-none focus:border-yellow-400/60 transition-colors placeholder:text-zinc-600"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiciosPreciosTab;
